/* ==========================================================================
   TUCANO · Cookie banner
   - Persistenza: localStorage + Shopify Customer Privacy API
   - Riapertura: window.tcnOpenCookieSettings() oppure click su [data-tcn-cookies-open]
   ========================================================================== */

(() => {
  'use strict';

  const STORAGE_KEY = 'tcn-cookie-consent';
  const VERSION = 1;

  const defaultPrefs = {
    necessary: true,
    analytics: false,
    marketing: false,
    version: VERSION,
    timestamp: null,
  };

  function loadPrefs() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (parsed.version !== VERSION) return null;
      return parsed;
    } catch (_) {
      return null;
    }
  }

  function savePrefs(prefs) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        ...prefs,
        version: VERSION,
        timestamp: Date.now(),
      }));
    } catch (_) {}
  }

  function applyShopifyConsent(prefs) {
    // Shopify Customer Privacy API
    if (window.Shopify && window.Shopify.customerPrivacy) {
      try {
        window.Shopify.customerPrivacy.setTrackingConsent(
          {
            analytics: !!prefs.analytics,
            marketing: !!prefs.marketing,
            preferences: !!prefs.analytics,
            sale_of_data: false,
          },
          () => {
            // success
          }
        );
      } catch (e) {
        console.warn('[Tucano cookies] Shopify Customer Privacy setTrackingConsent failed', e);
      }
    }

    // Custom event for any analytics scripts hooking in (GA, Meta, etc.)
    document.dispatchEvent(new CustomEvent('tcn:consent-updated', { detail: prefs }));
  }

  /* Rimuove la banner cookie automatica di Shopify (storefront-banner.js).
     Gestiamo noi il consenso via Shopify.customerPrivacy API. */
  function suppressShopifyBanner() {
    const SELECTORS = [
      'shopify-privacy-banner',
      'shopify-cookie-banner',
      'shopify-storefront-banner',
      '.shopify-privacy-banner',
      '.shopify-pc__banner',
      '#shopify-pc__banner',
      '#shopify-privacy-banner-container',
      '[data-shopify-privacy-banner]',
    ];
    const removeAll = () => {
      SELECTORS.forEach((sel) => {
        document.querySelectorAll(sel).forEach((el) => {
          try { el.remove(); } catch (_) { el.style.display = 'none'; }
        });
      });
    };
    removeAll();
    // Observer in caso lo script di Shopify lo inietti dopo
    const obs = new MutationObserver(() => removeAll());
    obs.observe(document.documentElement, { childList: true, subtree: true });
  }

  function init() {
    suppressShopifyBanner();

    const banner = document.querySelector('[data-tcn-cookies]');
    if (!banner) return;

    const compactView = banner.querySelector('[data-tcn-cookies-view="compact"]');
    const customizeView = banner.querySelector('[data-tcn-cookies-view="customize"]');
    const analyticsInput = banner.querySelector('input[data-tcn-cookies-category="analytics"]');
    const marketingInput = banner.querySelector('input[data-tcn-cookies-category="marketing"]');

    const stored = loadPrefs();

    // Mostra banner se non c'è consenso salvato
    if (!stored) {
      show();
    } else {
      // applica preferenze esistenti
      applyShopifyConsent(stored);
    }

    // Sync labels with checkbox state
    function syncToggleLabel(input) {
      const label = input.closest('.tcn-cookies__toggle')?.querySelector('[data-tcn-cookies-state]');
      if (label) label.textContent = input.checked ? 'on' : 'off';
    }
    [analyticsInput, marketingInput].forEach((inp) => {
      if (!inp) return;
      inp.addEventListener('change', () => syncToggleLabel(inp));
    });

    // Hydrate toggles from stored prefs when opening "customize" later
    function hydrateToggles() {
      const current = loadPrefs() || defaultPrefs;
      if (analyticsInput) { analyticsInput.checked = !!current.analytics; syncToggleLabel(analyticsInput); }
      if (marketingInput) { marketingInput.checked = !!current.marketing; syncToggleLabel(marketingInput); }
    }

    // Show / hide animation
    function show() {
      banner.hidden = false;
      // Force reflow then add class for transition
      void banner.offsetHeight;
      banner.classList.add('is-shown');
      hydrateToggles();
    }
    function hide() {
      banner.classList.remove('is-shown');
      setTimeout(() => { banner.hidden = true; }, 500);
    }

    // View switching
    function viewCompact() {
      compactView.hidden = false;
      customizeView.hidden = true;
    }
    function viewCustomize() {
      hydrateToggles();
      compactView.hidden = true;
      customizeView.hidden = false;
    }

    // Action handler
    banner.addEventListener('click', (e) => {
      const target = e.target.closest('[data-tcn-cookies-action]');
      if (!target) return;
      const action = target.dataset.tcnCookiesAction;

      if (action === 'accept-all') {
        const prefs = { ...defaultPrefs, analytics: true, marketing: true };
        savePrefs(prefs);
        applyShopifyConsent(prefs);
        hide();
      } else if (action === 'reject') {
        const prefs = { ...defaultPrefs, analytics: false, marketing: false };
        savePrefs(prefs);
        applyShopifyConsent(prefs);
        hide();
      } else if (action === 'customize') {
        viewCustomize();
      } else if (action === 'back') {
        viewCompact();
      } else if (action === 'save') {
        const prefs = {
          ...defaultPrefs,
          analytics: !!(analyticsInput && analyticsInput.checked),
          marketing: !!(marketingInput && marketingInput.checked),
        };
        savePrefs(prefs);
        applyShopifyConsent(prefs);
        hide();
      }
    });

    // Riapertura globale
    window.tcnOpenCookieSettings = () => {
      viewCustomize();
      show();
    };

    // Auto-bind on any [data-tcn-cookies-open] click
    document.addEventListener('click', (e) => {
      const opener = e.target.closest('[data-tcn-cookies-open]');
      if (opener) {
        e.preventDefault();
        window.tcnOpenCookieSettings();
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
